import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { getErrorDetail } from '@/utils/apiError';
import {
  useGetPlatformUserQuery,
  useUpdatePlatformUserMutation,
  useUpdatePlatformUserRolesMutation,
  useUpdatePlatformMembershipStatusMutation,
  useDeletePlatformUserMutation,
  PlatformUserDetail,
  PlatformUserMembership,
} from '@/redux/internal-services/platformAdminApi';

const STATUS_BADGE: Record<string, string> = {
  active:    'bg-green-100 text-green-800',
  invited:   'bg-blue-100 text-blue-800',
  inactive:  'bg-ui-subtle text-ui-text2',
  suspended: 'bg-yellow-100 text-yellow-800',
  deleted:   'bg-red-100 text-red-700',
};

const PLATFORM_ROLES = [
  { value: '', label: 'None (remove platform access)' },
  { value: 'platform_admin', label: 'Platform Admin' },
  { value: 'platform_support', label: 'Platform Support' },
];

const Avatar: React.FC<{ name?: string | null; url?: string | null; size?: 'sm' | 'lg' }> = ({ name, url, size = 'sm' }) => {
  const cls = size === 'lg' ? 'h-16 w-16 text-xl' : 'h-9 w-9 text-sm';
  if (url) return <img src={url} alt={name ?? ''} className={`${cls} rounded-full object-cover`} />;
  const initials = (name ?? '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className={`${cls} rounded-full bg-brand-light flex items-center justify-center text-brand-dark font-semibold flex-shrink-0`}>
      {initials}
    </div>
  );
};

// ── Membership row ────────────────────────────────────────────────────────────

interface MembershipRowProps {
  m: PlatformUserMembership;
  userId: string;
  onUpdated: () => void;
}

const MembershipRow: React.FC<MembershipRowProps> = ({ m, userId, onUpdated }) => {
  const [updateStatus, { isLoading }] = useUpdatePlatformMembershipStatusMutation();
  const [msg, setMsg] = useState('');

  const toggle = async () => {
    const newStatus = m.status === 'active' ? 'Suspended' : 'Active';
    try {
      await updateStatus({ userId, tenantId: m.tenant_id, status: newStatus }).unwrap();
      setMsg('');
      onUpdated();
    } catch {
      setMsg('Failed to update.');
    }
  };

  const isPlatform = m.tenant_id === 'platform';

  return (
    <tr className="border-b border-ui-border2 last:border-0">
      <td className="px-4 py-3">
        <div>
          <p className="text-sm font-medium text-ui-text">{m.tenant_name ?? m.tenant_id}</p>
          {isPlatform && <p className="text-xs text-ui-text3">Platform-level access</p>}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {m.role_names.length > 0 ? m.role_names.map((r) => (
            <span key={r} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full border border-purple-200">{r}</span>
          )) : <span className="text-xs text-ui-text3">No roles</span>}
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[m.status] ?? 'bg-ui-subtle text-ui-text2'}`}>
          {m.status}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-ui-text3">
        {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}
      </td>
      <td className="px-4 py-3 text-right">
        {!isPlatform && m.status !== 'deleted' && (
          <button
            onClick={toggle}
            disabled={isLoading}
            className={`text-xs border px-2.5 py-1 rounded transition disabled:opacity-50 ${
              m.status === 'active'
                ? 'text-yellow-600 border-yellow-200 hover:text-yellow-800'
                : 'text-green-600 border-green-200 hover:text-green-800'
            }`}
          >
            {isLoading ? '...' : m.status === 'active' ? 'Suspend' : 'Activate'}
          </button>
        )}
        {msg && <p className="text-xs text-red-500 mt-1">{msg}</p>}
      </td>
    </tr>
  );
};

// ── Edit Profile Section ──────────────────────────────────────────────────────

interface EditProfileProps {
  user: PlatformUserDetail;
  onUpdated: () => void;
}

const EditProfile: React.FC<EditProfileProps> = ({ user, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user.name ?? '', email: user.email ?? '', phone: user.phone ?? '' });
  const [error, setError] = useState('');
  const [updateUser, { isLoading }] = useUpdatePlatformUserMutation();

  const platformMembership = user.memberships.find((m) => m.tenant_id === 'platform');
  const [editRoles, setEditRoles] = useState(false);
  const [platformRole, setPlatformRole] = useState(platformMembership?.tenant_role_ids[0] ?? '');
  const [updateRoles, { isLoading: savingRoles }] = useUpdatePlatformUserRolesMutation();

  const handleSaveProfile = async () => {
    setError('');
    try {
      await updateUser({ userId: user.user_id, data: { name: form.name, email: form.email, phone: form.phone || undefined } }).unwrap();
      setEditing(false);
      onUpdated();
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? 'Failed to save.');
    }
  };

  const handleSaveRoles = async () => {
    try {
      await updateRoles({ userId: user.user_id, data: { platform_role: platformRole || null } }).unwrap();
      setEditRoles(false);
      onUpdated();
    } catch {
      setError('Failed to update roles.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile info */}
      <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-ui-text">Profile</h2>
          {!editing && (
            <button onClick={() => setEditing(true)} className="text-xs text-brand-dark hover:underline">Edit</button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-ui-text2 mb-1">Full Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text2 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text2 mb-1">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
                placeholder="Optional"
              />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={() => setEditing(false)} className="border border-ui-border text-ui-text2 px-3 py-1.5 rounded-lg text-xs hover:bg-ui-hover">Cancel</button>
              <button onClick={handleSaveProfile} disabled={isLoading} className="bg-brand-dark text-white px-3 py-1.5 rounded-lg text-xs disabled:opacity-50">
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <dl className="space-y-3">
            {[
              { label: 'Name', value: user.name },
              { label: 'Email', value: user.email },
              { label: 'Phone', value: user.phone ?? '—' },
              { label: 'Email Verified', value: user.email_verified ? 'Yes' : 'No' },
              { label: 'Account Active', value: user.is_active ? 'Yes' : 'No' },
              { label: 'Member Since', value: user.created_at ? new Date(user.created_at).toLocaleDateString() : '—' },
            ].map(({ label, value }) => (
              <div key={label} className="flex gap-4">
                <dt className="text-xs font-medium text-ui-text3 w-32 flex-shrink-0">{label}</dt>
                <dd className="text-sm text-ui-text">{value ?? '—'}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {/* Platform role */}
      <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-ui-text">Platform Role</h2>
          {!editRoles && (
            <button onClick={() => setEditRoles(true)} className="text-xs text-brand-dark hover:underline">Edit</button>
          )}
        </div>

        {editRoles ? (
          <div className="space-y-3">
            <select
              value={platformRole}
              onChange={(e) => setPlatformRole(e.target.value)}
              className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
            >
              {PLATFORM_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => setEditRoles(false)} className="border border-ui-border text-ui-text2 px-3 py-1.5 rounded-lg text-xs hover:bg-ui-hover">Cancel</button>
              <button onClick={handleSaveRoles} disabled={savingRoles} className="bg-brand-dark text-white px-3 py-1.5 rounded-lg text-xs disabled:opacity-50">
                {savingRoles ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ui-text">
            {platformMembership?.role_names[0] ?? <span className="text-ui-text3">No platform role</span>}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────

const UserDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  const { data, isLoading, refetch } = useGetPlatformUserQuery(userId ?? '', { skip: !userId });
  const [deleteUser, { isLoading: deleting }] = useDeletePlatformUserMutation();

  const user: PlatformUserDetail | undefined = data?.data;

  const handleDelete = async () => {
    if (!userId) return;
    try {
      await deleteUser(userId).unwrap();
      navigate('/platform/users');
    } catch (err: unknown) {
      setActionMsg({ type: 'error', text: getErrorDetail(err) ?? 'Delete failed.' });
      setDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <PageShell maxWidth="max-w-5xl" header={<div className="h-8" />}>
        <div className="py-20 text-center text-ui-text3 text-sm">Loading user...</div>
      </PageShell>
    );
  }

  if (!user) {
    return (
      <PageShell maxWidth="max-w-5xl" header={<div className="h-8" />}>
        <div className="py-20 text-center text-ui-text3 text-sm">{t('platform.userNotFound')}</div>
      </PageShell>
    );
  }

  const tenantMemberships = user.memberships.filter((m) => m.tenant_id !== 'platform');

  return (
    <PageShell
      maxWidth="max-w-5xl"
      header={
        <>
          <button
            onClick={() => navigate('/platform/users')}
            className="flex items-center gap-1.5 text-sm text-ui-text2 hover:text-brand-dark mb-3 transition"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Users
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <Avatar name={user.name} url={user.profile_picture_url} size="lg" />
              <div>
                <h1 className="text-2xl font-bold text-ui-text">{user.name ?? '—'}</h1>
                <p className="text-sm text-ui-text2">{user.email}</p>
                {!user.is_active && (
                  <span className="inline-flex mt-1 items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Deleted</span>
                )}
              </div>
            </div>
            {user.is_active && (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="inline-flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium transition"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete User
              </button>
            )}
          </div>
        </>
      }
    >

      {actionMsg && (
        <p className={`mb-6 text-sm ${actionMsg.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{actionMsg.text}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: profile + platform role */}
        <EditProfile user={user} onUpdated={refetch} />

        {/* Right: tenant memberships */}
        <div className="space-y-6">
          <div className="bg-ui-card border border-ui-border2 rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-ui-border2">
              <h2 className="text-base font-semibold text-ui-text">Tenant Memberships</h2>
              <p className="text-xs text-ui-text3 mt-0.5">{tenantMemberships.length} tenant{tenantMemberships.length !== 1 ? 's' : ''}</p>
            </div>
            {tenantMemberships.length === 0 ? (
              <div className="py-10 text-center text-ui-text3 text-sm">No tenant memberships.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-ui-raised border-b border-ui-border2">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Tenant</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Roles</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-ui-text2 uppercase tracking-wider hidden sm:table-cell">Joined</th>
                      <th className="px-4 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantMemberships.map((m) => (
                      <MembershipRow key={m.tenant_id} m={m} userId={user.user_id} onUpdated={refetch} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-ui-card rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-bold text-ui-text mb-2">Delete User</h3>
            <p className="text-sm text-ui-text2 mb-6">
              Are you sure you want to delete <strong>{user.name ?? user.email}</strong>? This is a soft delete — all history is preserved but the user will lose access.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(false)} className="flex-1 border border-ui-border text-ui-text py-2 rounded-lg text-sm hover:bg-ui-hover transition">Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm font-medium transition disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default UserDetailPage;
